import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {TimeAccountDashboardQueryParams} from "@/shared/queryParams";
import * as Constants from "@/shared/constants";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {ModalIsland} from "@/shared/reactIslandModal";
import {PasswordStrengthValidation} from "@/features/auth/passwordStrengthValidation";
import {ChangeUserPasswordModal, ChangeUserPasswordModalProps} from "./changeUserPasswordModal";
import {AddUserDialogHandler, AddUserMode} from "./addUserDialog";
import {ExternalUserSyncDialog} from "./externalUserSyncDialog";
import {UserDialogHandler} from "./userDialog";
import {UserChangeConfigurationDialogHandler} from "@/features/user/userChangeConfigurationDialog";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {debounce} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {User} from "@/shared/timr";
import {ExportType, InvitationStatus, MailDeliveryStatus, TimeAccountState, WorkScheduleModelFilter} from "@/shared/timrTypes";
import Modal = BootstrapModal.Modal;

interface UsersPageConfig {
	externalAuthOnly: boolean;
	passwordStrengthValidation?: PasswordStrengthValidation;
}

interface ActiveAndLicensedUserResponse {
	activeUserCount: number;
	licensedUserCount: number | null;
}

export class UsersAdministrationPage {
	private userRightsContent = $("#userRightsContent");
	private table: UsersTable;
	private changePasswordModal: ModalIsland<ChangeUserPasswordModalProps>;
	private inviteDialog: InviteDialogHandler;
	private userDialog: UserDialogHandler;
	private userChangeConfigurationDialogHandler: UserChangeConfigurationDialogHandler;
	private addUserDialog: AddUserDialogHandler;
	private externalUserSyncDialog: ExternalUserSyncDialog;
	private groupDropdown: GroupDropdown;

	constructor(config: UsersPageConfig) {
		this.table = new UsersTable({
			element: $("#usersTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});
		UsersAdministrationPage.reloadUserCountInfoPanel();

		const userExportButton = $("#userExportButton");

		this.table.getDataTable().on("init.dt", () => {
			userExportButton.show();
			$(".reportExportButtonLoadingIndicator").hide();
		});
		this.table.getDataTable().on("processing.dt", (e, settings, processing) => {
			if (processing) {
				Button.setLoading(userExportButton);
			} else {
				Button.resetLoading(userExportButton);
			}
		});
		this.changePasswordModal = new ModalIsland<ChangeUserPasswordModalProps>(ChangeUserPasswordModal, {
			// The user is only known once the modal is opened for a table row, see openChangePasswordModal().
			userId: null,
			hasConnectedMobileDevices: false,
			passwordStrengthValidation: config.passwordStrengthValidation,
			onPasswordChanged: () => this.table.reload(),
		});
		this.inviteDialog = new InviteDialogHandler();
		this.userChangeConfigurationDialogHandler = new UserChangeConfigurationDialogHandler();
		this.userDialog = new UserDialogHandler(this.userChangeConfigurationDialogHandler);
		this.addUserDialog = new AddUserDialogHandler({
			initialMode: config.externalAuthOnly ? AddUserMode.TerminalQrCode : AddUserMode.Invite,
			passwordStrengthValidation: config.passwordStrengthValidation,
		});
		this.externalUserSyncDialog = new ExternalUserSyncDialog();

		$(this.table).on(UsersTable.EVENT_CHANGE_PASSWORD, (event, user: User) => {
			this.openChangePasswordModal(user);
		}).on(BasicDataTable.EVENT_DBLCLICK + " " + UsersTable.EVENT_EDIT_USER, (event, user: User) => {
			this.userDialog.setUser(user);
			this.userDialog.open();
		}).on(UsersTable.EVENT_RESEND_INVITATION, (event, user: User) => {
			this.resendInvitation(user);
		}).on(UsersTable.EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST, (event, user: User) => {
			this.removeUserFromSuppressionList(user);
		});

		$(this.userDialog)
			.on(UserDialogHandler.EVENT_SAVED_USER, () => {
				this.reloadUserList();
			})
			.on(UserDialogHandler.EVENT_RESEND_INVITATION, (event, user: User) => {
				this.resendInvitation(user);
			})
			.on(UserDialogHandler.EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST, (event, user: User) => {
				this.removeUserFromSuppressionList(user);
			});

		$(this.userChangeConfigurationDialogHandler)
			.on(UserChangeConfigurationDialogHandler.EVENT_SAVED_CONFIGURATION, () => {
				this.reloadUserList();
			});

		$(this.addUserDialog).on(UserDialogHandler.EVENT_SAVED_USER, () => this.reloadUserList());

		$(this.inviteDialog).on(InviteDialogHandler.EVENT_SENT_INVITE, () => this.reloadUserList());
		$(this.externalUserSyncDialog).on(ExternalUserSyncDialog.EVENT_USERS_CHANGED, () => this.table.reload());

		$("#usersInviteButton").click(() => {
			if (this.isAddUserAllowed()) {
				this.inviteDialog.open();
			}
		});

		const filterTable = () => this.table.filter(this.buildTableFilterData());
		$("#usersFilterResigned").change(filterTable);
		$("#workScheduleModelSelect").change(filterTable);
		$("#userRoleSelect").change(filterTable);

		$("#usersFilterName").on("input", debounce(filterTable, this, 350));

		$("#usersAddButton").click(() => {
			if (this.isAddUserAllowed()) {
				this.addUserDialog.open();
			}
		});
		$(".openExternalUserSyncDialogButton").click((e) => {
			this.externalUserSyncDialog.setIntegration($(e.currentTarget).data("integration") as string);
			this.externalUserSyncDialog.open();
		});

		this.groupDropdown = new GroupDropdown({
				rootElement: $("#userListGroupDropdown"),
				allowSelectionOfNotAssigned: true,
				emptyMessage: messages["group.filter.all"],
				clearEntryMessage: messages["user.filter.all"]
			},
			// overwrite default URLs for group dropdown --> admin should see all groups in the User administration
			{
				loadRootNodesURL: "admin/loadAllGroups.json",
				loadSubNodesURL: "admin/loadSubGroups.json",
				filterNodesURL: "admin/filterGroups.json"
			}
		);

		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, filterTable);

		this.userRightsContent.find("#userRoleSelect")
			.multiselect({
				includeSelectAllOption: true,
				numberDisplayed: 1, // one selected item is displayed by text, another ones by 'n selected' in the input field
				selectAllText: messages["filter.all"],
				allSelectedText: messages["filter.all"],
				nonSelectedText: messages["filter.all"],
				nSelectedText: messages["filter.select.n"],
				selectedClass: "", // selected elements are only visualized by checkmark (not by extra class)
				buttonWidth: "100%", // input field should have the full length as parent container
				onSelectAll: () => this.multiSelectOnChange(),
				onChange: () => this.multiSelectOnChange()
			});

		$("#userExportDropDown a").click(e => this.downloadUserExport($(e.currentTarget).data("exportType") as ExportType));
	}

	private multiSelectOnChange() {
		// no-op, kept for compatibility with multiselect event handlers
	}

	public static reloadUserCountInfoPanel() {
		const loadingIndicator = $("#userCountInfoPanel .overlay-processing");
		loadingIndicator.show();
		api("admin/countActiveAndLicencedUsers.json")
			.get()
			.then((response: ActiveAndLicensedUserResponse) => {
				$("#activeUserCount").text(response.activeUserCount);
				$("#licensedUserCount").text(response.licensedUserCount ?? "");
			})
			.always(() => loadingIndicator.hide());
	}

	private reloadUserList() {
		UsersAdministrationPage.reloadUserCountInfoPanel();
		this.table.reload();
	}

	private isAddUserAllowed(): boolean {
		const isAddUserAllowed = SecurityStatus.isAddUserAllowed();
		if (!isAddUserAllowed) {
			BootstrapModal.warn({
				title: "limit.user.title",
				message: "limit.user"
			});
		}
		return isAddUserAllowed;
	}

	private buildTableFilterData() {
		const workScheduleModelSelectValue = $("#workScheduleModelSelect").val() as string;
		const parsedValue = parseInt(workScheduleModelSelectValue, 10);
		const workScheduleModelId: number | null = isNaN(parsedValue) ? null : parsedValue;

		let workScheduleModelFilter: WorkScheduleModelFilter;
		switch (workScheduleModelId) {
			case -1:
				workScheduleModelFilter = WorkScheduleModelFilter.NONE;
				break;
			case null:
				workScheduleModelFilter = null;
				break;
			default:
				workScheduleModelFilter = WorkScheduleModelFilter.EXACT;
		}

		const resignedSelectValue = $("#usersFilterResigned").val();

		const filterParams = [
			{
				name: "name",
				value: $("#usersFilterName").val() as string
			},
			{
				name: "resigned",
				value: resignedSelectValue === "undefined" ? null : resignedSelectValue === "true"
			},
			{
				name: "workScheduleModelId",
				value: workScheduleModelId
			},
			{
				name: "workScheduleModelFilter",
				value: workScheduleModelFilter
			},
			{
				name: "groupId",
				value: this.groupDropdown?.getSelectedId()
			}
		];

		// add user roles to filter params
		($("#userRoleSelect").val() as string[]).forEach(role => {
			switch (role) {
				case "ROLE_ADMINISTRATOR":
					filterParams.push({name: "admin", value: true})
					break;
				case "ROLE_TASKMANAGER":
					filterParams.push({name: "taskManager", value: true})
					break;
				case "ROLE_TASKLEADER":
					filterParams.push({name: "taskLeader", value: true})
					break;
				case 'ROLE_OVERALLTASKLEADER':
					filterParams.push({name: "overallTaskLeader", value: true})
					break;
				case 'ROLE_SUBSCRIPTION_MANAGER':
					filterParams.push({name: "subscriptionManager", value: true})
					break;
			}
		})
		return filterParams;
	}

	/**
	 * Setting a password manually cancels a still pending invitation, so that case is confirmed first.
	 * @private
	 */
	private openChangePasswordModal(user: User) {
		if (user.invitation?.status === InvitationStatus.Open) {
			BootstrapModal.confirm({
				title: "dialog.warning.title",
				message: "dialog.confirm.changePassword",
				yesLabel: "confirmation.setPassword",
				noLabel: "confirmation.cancel",
				yesAction: (modal) => {
					this.showChangePasswordModal(user);
					modal.close();
				}
			});
		} else {
			this.showChangePasswordModal(user);
		}
	}

	private showChangePasswordModal(user: User) {
		api("admin/checkIfMobileDevicesForUserExists.json")
			.get({userId: user.id})
			.done(data => {
				this.changePasswordModal.Open({
					userId: user.id,
					hasConnectedMobileDevices: data.connectedMobileDevices > 0,
				});
			});
	}

	private resendInvitation(user: User) {
		api("admin/resendInvitation.json")
			.post({
				id: user.id
			})
			.done(() => {
				this.reloadUserList();
				BootstrapModal.info({
					title: "invite.resend.confirm.title",
					message: "invite.resend.confirm.message"
				});
			});
	}

	private removeUserFromSuppressionList(user: User) {
		api("admin/removeUserFromSuppressionList.json")
			.post({
				id: user.id
			})
			.done(() => {
				this.reloadUserList();
				BootstrapModal.info({
					title: "email.reactivate.confirm.title",
					message: "email.reactivate.confirm.message"
				});
			});
	}

	private downloadUserExport(exportType: ExportType) {
		const params = this.buildTableFilterData();
		params.push({name: "exportFileType", value: exportType});

		void api("admin/export/users.json")
			.post(params)
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportStatusBaseUrl: "export/status.json",
						exportType: exportType,
						loadingMessage: messages["downloadStatus.loadingMessage.reports"],
						allowCancellation: true
					});
				}
			});
	}
}


class UsersTable extends BasicDataTable<User> {
	public static EVENT_RESEND_INVITATION = "event_resend_invitation";
	public static EVENT_CHANGE_PASSWORD = "event_change_password";
	public static EVENT_EDIT_USER = "event_edit_user";
	public static EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST = "event_remove_user_from_suppression_list";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/users.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row, data, index) => {
				const user = data as User;
				if (user.disabled) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<User>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "firstname", orderable: true, render: BasicDataTable.renderText},
			{data: "lastname", orderable: true, render: BasicDataTable.renderText},
			{data: "login", orderable: true, render: BasicDataTable.renderText},
			{data: "email", orderable: true, render: BasicDataTable.renderText},
			{
				data: "workScheduleModelName",
				orderable: false,
				render: this.renderWorkScheduleModelOrInitButton,
				visible: globals.workingTimeEnabled
			},
			{data: "vacationDaysPerYear", orderable: true, render: this.renderDays, visible: globals.workingTimeEnabled},
			{
				data: "admin",
				orderable: false,
				render: this.renderPermissionIcons,
				className: "slim-icon-column no-wrap-column"
			},
			{
				data: "invitation",
				orderable: false,
				render: this.renderInvitationStatus,
				className: "slim-icon-column no-wrap-column"
			},
			{
				data: "disabled",
				orderable: false,
				render: this.renderResignedIcon,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[2, "asc"]];
	}

	protected createContextMenu() {
		const dataTableContextMenu = super.createContextMenu()
			.addMenuEntry({
				label: "users.context.edit",
				action: user => { $(this).triggerHandler(UsersTable.EVENT_EDIT_USER, user); }
			});
		if (SecurityStatus.isPasswordChangeAllowed()) {
			dataTableContextMenu
				.addMenuEntry({
					label: "users.context.changePassword",
					action: user => { $(this).triggerHandler(UsersTable.EVENT_CHANGE_PASSWORD, user); }
				});
		}
		dataTableContextMenu
			.addDivider()
			.addMenuEntry({
				label: "users.context.enable",
				action: user => this.enableUser(user),
				show: user => user.disabled
			})
			.addMenuEntry({
				label: "users.context.disable",
				action: user => this.disableUser(user),
				show: user => !user.disabled
			})
			.addDivider()
			.addMenuEntry({
				label: "users.context.resendInvitation",
				action: user => { $(this).triggerHandler(UsersTable.EVENT_RESEND_INVITATION, user); },
				show: user => user.invitation && user.invitation.status === InvitationStatus.Open
			})
			.addMenuEntry({
				label: "users.context.reactivateEmail",
				action: user => { $(this).triggerHandler(UsersTable.EVENT_REMOVE_USER_FROM_SUPPRESSION_LIST, user); },
				show: user => user.mailDeliveryStatus === MailDeliveryStatus.BOUNCED && user.invitation && user.invitation.status !== InvitationStatus.Open
			})
			.addDivider()
			.addMenuEntry({
				label: "user.context.deleteuser",
				action: user => this.deleteUser(user)
			});
		return dataTableContextMenu;
	}

	private renderResignedIcon(this: void, data: boolean, type: string, row: User, meta: DataTables.CellMetaSettings) {
		return data ? `<i class="fal fa-archive" title="${messages["user.filter.resignDate.yes"]}"></i>` : "";
	}

	private renderInvitationStatus(this: void, data: { status: InvitationStatus } | null | undefined, type: string, row: User, meta: DataTables.CellMetaSettings) {
		if (data && data.status === InvitationStatus.Open) {
			return `<i class="fal fa-paper-plane position-left" title="${messages["admin.user.status.pendingInvitation"]}"></i>
					${messages["admin.user.status.pendingInvitation"]}
					${row.mailDeliveryStatus === MailDeliveryStatus.BOUNCED ? "- " + messages["admin.user.status.emailNotDelivered"] : ""}`;
		} else if (data && data.status !== InvitationStatus.Open && row.mailDeliveryStatus === MailDeliveryStatus.BOUNCED) {
			return `<i class="fal fa-exclamation-triangle position-left" title="${messages["admin.user.status.emailNotDelivered"]}"></i>
					${messages["admin.user.status.emailDeactivated"]}`;
		} else {
			return "";
		}
	}

	private renderDays(this: void, data: number, type: string, row: User, meta: DataTables.CellMetaSettings) {
		if (data != null) {
			return data + " " + messages["user.crud.vacationDaysPerYearUnit"];
		} else {
			return "";
		}
	}

	private renderPermissionIcons(this: void, data: boolean, type: string, user: User, meta: DataTables.CellMetaSettings) {
		let render = "";

		if (globals.projectTimeEnabled) {
			if (user.taskManager) {
				render += `<i class="fal fa-sitemap mr-5" title="${messages["user.crud.taskManager"]}"></i>`;
			}

			if (user.overallTaskLeader) {
				render += `<i class="fal fa-user-friends mr-5" title="${messages["user.crud.overallTaskLeader"]}"></i>`;
			} else if (user.taskLeader) {
				render += `<i class="fal fa-user-tie mr-5" title="${messages["user.crud.taskLeader"]}"></i>`;
			}
		}

		if (user.admin) {
			render += `<i class="fal fa-user-crown" title="${messages["user.crud.admin"]}"></i>`;
		}

		return render;
	}

	private renderWorkScheduleModelOrInitButton(this: void, data: string, type: string, user: User, meta: DataTables.CellMetaSettings) {
		if (user.timeAccountState === TimeAccountState.NO_TIME_ACCOUNT && !user.disabled) {
			const link = TimeAccountDashboardQueryParams.buildLink({
				userFilter: user.id,
			}, "timeaccounts/timeAccountDashboard.html", "initializeTimeAccount");
			return `<a href="${link}" class="btn btn-primary btn-xs" title="${messages["admin.timeaccount.init"]}">${messages["admin.timeaccount.init"]}</a>`
		}

		return htmlEscape(data);
	}

	private enableUser(user: User) {
		// Active User through time account exit deletion
		if (user.timeAccountState === TimeAccountState.EXIT) {
			BootstrapModal.warn({
				title: "dialog.warning.title",
				message: $("<p>").html(messages["user.crud.enable.info"]),
				buttons: [
					{
						text: "user.goToTimeAccount",
						css: "btn-primary",
						action: () => {
							window.location.href = TimeAccountDashboardQueryParams.buildLink({
								userFilter: user.id,
								showInactiveUsers: true
							}, "timeaccounts/timeAccountDashboard.html");
						}
					},
					{
						text: "dialog.cancel",
						css: "btn-default",
					}
				]
			});
			return;
		}

		api("admin/enableUser.json")
			.post({
				id: user.id
			})
			.done(data => {
				SecurityStatus.update(data.securityStatus);
				UsersAdministrationPage.reloadUserCountInfoPanel();
				this.reload();
			});
	}

	private disableUser(user: User) {
		const performDisableUser = () => {
			return api("admin/disableUser.json")
				.post({
					id: user.id
				})
				.done(data => {
					SecurityStatus.update(data.securityStatus);
					UsersAdministrationPage.reloadUserCountInfoPanel();
					this.reload();
				});
		};

		// Deactivate User through time account exit
		if (user.timeAccountState !== TimeAccountState.NO_TIME_ACCOUNT) {
			BootstrapModal.warn({
				message: $("<p>").html(messages["user.crud.disable.info"]),
				buttons: [
					{
						text: "report.timeAccountDashboard.actionButtons.createExitBooking",
						css: "btn-primary",
						action: () => {
							// Navigate to time account dashboard and open the create exit dialog
							window.location.href = TimeAccountDashboardQueryParams.buildLink({
								userFilter: user.id
							}, "timeaccounts/timeAccountDashboard.html", "exitUser");
						}
					},
					{
						text: "dialog.cancel",
						css: "btn-default",
					}
				]
			});
			return;
		}

		api("admin/checkIfRunningRecordsExistsForUser.json")
			.get({
				id: user.id
			}).done(data => {
			if (data.foundRecordings === 0) {
				performDisableUser();
			} else {
				BootstrapModal.confirm({
					title: "dialog.warning.title",
					message: "dialog.confirm.disable.user",
					yesAction: ((modal, button) => {
						Button.setLoading(button);
						performDisableUser().always(() => {
							Button.resetLoading(button);
							modal.close();
						});
					}),
					yesLabel: "confirmation.disable",
					noLabel: "confirmation.cancel"
				});
			}
		});
	}

	private deleteUser(user: User) {
		const performDeleteUser = () => {
			return api("admin/deleteUser.json")
				.post({
					id: user.id
				})
				.done(data => {
					SecurityStatus.update(data.securityStatus);
					UsersAdministrationPage.reloadUserCountInfoPanel();
					this.reload();
				});
		};

		const showDeleteUserDialog = () => {
			const confirm = BootstrapModal.confirm({
				title: "dialog.warning.title",
				message: `<form class="form-horizontal">
									<div>${messages["dialog.confirm.delete.user"]}</div>
									<div class="form-group mt-5">
										<label class="col-lg-12" for="confirmDeleteTextInput">
											${messages.withArguments("dialog.confirm.delete.user.info", htmlEscape(user.fullname))}
										</label>
										<div class="col-lg-12">
											<input type="text" id="confirmDeleteTextInput" class="form-control" />
										</div>
									</div>
								</form>`,
				destroyOnClose: false,
				escapeText: false,
				yesLabel: "confirmation.delete",
				yesCss: "deleteButton btn-danger",
				yesAction: (modal, button) => {
					const confirmDeleteTextInput = modal.find("#confirmDeleteTextInput");
					Input.removeFeedback(confirmDeleteTextInput);

					if (confirmDeleteTextInput.val() === messages["dialog.confirm.delete.user.comparison"]) {
						performDeleteUser().always(() => {
							Button.resetLoading(button);
							modal.close();
						});
					} else {
						Input.addFeedback(confirmDeleteTextInput, "has-error", messages["dialog.confirm.delete.user.error"]);
						setTimeout(() => Button.resetLoading(button), 200);
					}
				},
				noLabel: "confirmation.cancel"
			});
			confirm.getDomModal().find("form").submit(e => e.preventDefault());
		};

		const showExistingRecordingsError = () => {
			BootstrapModal.confirm({
				title: "dialog.error.delete.user.title",
				message: "dialog.error.delete.user",
				escapeText: false,
				yesLabel: "user.context.disableuser",
				yesCss: "yesButton btn-primary",
				noLabel: "confirmation.cancel",
				yesAction: (modal) => {
					modal.close();
					if (modal.getCheckboxes("deleteAnywayCheckbox").is(":checked")) {
						showDeleteUserDialog();
					} else {
						this.disableUser(user);
					}
				},
				buttons: [
					// DEVNOTE: Two empty objects are needed to keep the button layout
					// otherwise the buttons would be overridden by the checkbox
					{},
					{},
					{
						text: "dialog.error.delete.user.anyway",
						left: true,
						isCheckbox: true,
						css: "deleteAnywayCheckbox",
						action: (modal, checkbox) => {
							const checked = checkbox.is(":checked");
							modal.getButtons("yesButton")
								.toggleClass("btn-primary", !checked)
								.toggleClass("btn-danger", checked)
								.text(checked ? messages["user.context.deleteuser"] : messages["user.context.disableuser"]);
						}
					}
				]
			});
		};

		api("admin/checkIfRecordsExistsForUser.json")
			.get({
				id: user.id
			})
			.done(data => {
				if (data.foundRecordings !== 0) {
					showExistingRecordingsError();
				} else {
					showDeleteUserDialog();
				}
			});
	}
}

class InviteDialogHandler {
	public static EVENT_SENT_INVITE = "event_sent_invite";
	private modal: Modal;

	constructor() {
		this.modal = new Modal({
			title: "invite.header",
			message: $("#inviteDialog"),
			buttons: [
				{
					text: "invite.send",
					action: (modal, button) => this.sendInvites(modal, button)
				},
				{
					text: "dialog.cancel"
				}
			],
			onOpen: () => {
				$("#inviteEmails").val("").focus();
			}
		});
	}

	public open() {
		this.modal.show();
	}

	private sendInvites(modal: Modal, button: JQuery) {
		Button.setLoading(button);
		api("admin/sendInvitations.json")
			.postForm("#inviteForm")
			.done((data) => {
				SecurityStatus.update(data.securityStatus);
				$(this).triggerHandler(InviteDialogHandler.EVENT_SENT_INVITE);
				modal.close();
			}).always(() => {
			Button.resetLoading(button);
		});
	}
}
